import { create } from 'zustand';
import { API_URL } from '@/network/http';
import type { IngestStatus } from '../types';
import {
  fetchMediaAssets,
  uploadMediaFile,
  deleteMediaAsset as apiDeleteMediaAsset,
  type MediaAssetDTO,
} from '../api/mediaApi';

/** Resolve a relative API path to a full URL for use in <img src> etc. */
function resolveUrl(path: string | null | undefined): string | null {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return `${API_URL}${path}`;
}

/**
 * Workspace-level media library store.
 * Backed by R2 storage + D1 database.
 * Assets here are shared across all projects — they only enter a project
 * when the user drags them onto the canvas or timeline.
 */

export interface LibraryAsset {
  id: string;
  filename: string;
  mimeType: string;
  publicUrl: string;
  r2Key: string;
  fileSize: number;
  durationMs: number;
  width: number;
  height: number;
  mediaType: string;
  thumbnailUrl: string | null;
  // Client-side upload state
  ingestStatus: IngestStatus;
  ingestProgress: number;
  ingestError?: string;
}

function dtoToAsset(dto: MediaAssetDTO): LibraryAsset {
  return {
    id: dto.id,
    filename: dto.filename,
    mimeType: dto.mimeType,
    publicUrl: resolveUrl(dto.publicUrl) || dto.publicUrl,
    r2Key: dto.r2Key,
    fileSize: dto.fileSize,
    durationMs: dto.durationMs,
    width: dto.width,
    height: dto.height,
    mediaType: dto.mediaType,
    thumbnailUrl: resolveUrl(dto.thumbnailUrl),
    ingestStatus: dto.status === 'ready' ? 'ready' : 'error',
    ingestProgress: 100,
  };
}

interface MediaLibraryStore {
  assets: LibraryAsset[];
  _loaded: boolean;

  loadFromBackend: () => Promise<void>;
  uploadFile: (file: File) => Promise<void>;
  removeAsset: (assetId: string) => Promise<void>;
  getAsset: (assetId: string) => LibraryAsset | undefined;
}

export const useMediaLibraryStore = create<MediaLibraryStore>((set, get) => ({
  assets: [],
  _loaded: false,

  loadFromBackend: async () => {
    if (get()._loaded) return;
    try {
      const dtos = await fetchMediaAssets();
      set({
        assets: dtos.map(dtoToAsset),
        _loaded: true,
      });
    } catch (err) {
      console.error('Failed to load media library:', err);
      set({ _loaded: true });
    }
  },

  uploadFile: async (file: File) => {
    // Add temporary placeholder
    const tempId = `uploading-${crypto.randomUUID()}`;
    const placeholder: LibraryAsset = {
      id: tempId,
      filename: file.name,
      mimeType: file.type,
      publicUrl: '',
      r2Key: '',
      fileSize: file.size,
      durationMs: 0,
      width: 0,
      height: 0,
      mediaType: file.type.startsWith('audio/') ? 'audio' : 'video',
      thumbnailUrl: null,
      ingestStatus: 'uploading',
      ingestProgress: 0,
    };
    set((s) => ({ assets: [placeholder, ...s.assets] }));

    try {
      const dto = await uploadMediaFile(file, (pct) => {
        set((s) => ({
          assets: s.assets.map((a) =>
            a.id === tempId ? { ...a, ingestProgress: pct } : a,
          ),
        }));
      });

      // Replace placeholder with real asset
      const asset = dtoToAsset(dto);
      set((s) => ({
        assets: s.assets.map((a) => (a.id === tempId ? asset : a)),
      }));
    } catch (err) {
      // Mark as error
      set((s) => ({
        assets: s.assets.map((a) =>
          a.id === tempId
            ? { ...a, ingestStatus: 'error' as IngestStatus, ingestError: String(err) }
            : a,
        ),
      }));
      console.error('Upload failed:', err);
    }
  },

  removeAsset: async (assetId: string) => {
    // Optimistic removal
    set((s) => ({ assets: s.assets.filter((a) => a.id !== assetId) }));
    try {
      await apiDeleteMediaAsset(assetId);
    } catch (err) {
      console.error('Delete failed:', err);
      // Could restore on failure, but keep it simple
    }
  },

  getAsset: (assetId) => get().assets.find((a) => a.id === assetId),
}));
